import { useState, useEffect } from 'react';
import type { FC, ReactElement } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Divider,
  Button,
  Chip,
  Stack,
  Avatar,
  IconButton,
  Card,
  CardContent,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import {
  Person as PersonIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  AccessTime as AccessTimeIcon,
  CalendarToday as CalendarTodayIcon,
  Security as SecurityIcon,
  SupervisorAccount as SupervisorAccountIcon,
  KeyboardBackspace as KeyboardBackspaceIcon
} from '@mui/icons-material';
import { User } from '../../types/user';
import { usersApi } from '../../services/api';

const formatDate = (dateString?: string | Date): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('ar-SA', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const UserDetails: FC = (): ReactElement => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        if (!id) {
          setError('معرف المستخدم غير صحيح');
          setLoading(false);
          return;
        }
        
        const userData = await usersApi.getUser(id);
        setUser(userData);
      } catch (err) {
        console.error('Error fetching user:', err);
        setError('حدث خطأ أثناء تحميل بيانات المستخدم');
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [id]);

  const handleDelete = async () => {
    try {
      if (id) {
        await usersApi.deleteUser(id);
        navigate('/users');
      }
    } catch (err) {
      console.error('Error deleting user:', err);
      setError('حدث خطأ أثناء حذف المستخدم');
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>جارِ التحميل...</Typography>
      </Box>
    );
  }

  if (error || !user) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error || 'لم يتم العثور على المستخدم'}</Alert>
        <Button sx={{ mt: 2 }} variant="outlined" onClick={() => navigate('/users')}>
          العودة إلى قائمة المستخدمين
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, bgcolor: 'grey.50', minHeight: '100vh' }}>
      {/* Header with navigation */}
      <Box 
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mb: 3,
          alignItems: 'center'
        }}
      >
        <Button
          startIcon={<KeyboardBackspaceIcon />}
          onClick={() => navigate('/users')}
          variant="outlined"
          sx={{
            borderRadius: 2,
            borderColor: 'grey.300',
            color: 'text.secondary',
            '&:hover': {
              borderColor: 'grey.400',
              bgcolor: 'rgba(0, 0, 0, 0.02)'
            }
          }}
        >
          العودة للقائمة
        </Button>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            startIcon={<EditIcon />}
            variant="outlined"
            color="primary"
            onClick={() => navigate(`/users/edit/${id}`)}
            sx={{ borderRadius: 2 }}
          >
            تعديل
          </Button>
          <Button
            startIcon={<DeleteIcon />}
            variant="outlined"
            color="error"
            onClick={() => setDeleteDialogOpen(true)}
            sx={{ borderRadius: 2 }}
          >
            حذف
          </Button>
        </Box>
      </Box>

      {/* Main content */}
      <Paper
        elevation={0}
        sx={{
          p: 0,
          borderRadius: 3,
          overflow: 'hidden',
          boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
          maxWidth: 1200,
          mx: 'auto'
        }}
      >
        {/* User header with avatar */}
        <Box
          sx={{
            bgcolor: 'secondary.main',
            color: 'white',
            p: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexDirection: { xs: 'column', sm: 'row' },
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              right: 0,
              bottom: 0,
              width: '100%',
              height: '40%',
              background: 'linear-gradient(to top, rgba(0,0,0,0.1), transparent)',
              zIndex: 1
            }
          }}
        >
          <Avatar
            sx={{
              width: 80,
              height: 80,
              bgcolor: 'primary.main',
              boxShadow: '0 4px 14px rgba(0,0,0,0.2)',
              border: '3px solid #fff'
            }}
          >
            <PersonIcon sx={{ fontSize: 40 }} />
          </Avatar>
          
          <Box sx={{ flexGrow: 1, zIndex: 2 }}>
            <Typography variant="h4" component="h1" fontWeight="bold">
              {user.fullName}
            </Typography>
            
            <Stack direction="row" spacing={1} mt={1} alignItems="center">
              <Chip
                icon={<PersonIcon style={{ color: 'inherit' }} />}
                label={`@${user.username}`}
                size="small"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  fontWeight: 'medium',
                  px: 0.5
                }}
              />
              
              <Chip
                label={user.status === 'active' ? 'نشط' : 'غير نشط'}
                size="small"
                sx={{
                  bgcolor: user.status === 'active'
                    ? 'success.main'
                    : 'error.main',
                  color: 'white',
                  fontWeight: 'medium',
                  px: 0.5
                }}
              />
            </Stack>
          </Box>

          <Box sx={{ zIndex: 2 }}>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              تاريخ الإنشاء: {formatDate(user.createdAt)}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              آخر تحديث: {formatDate(user.updatedAt)}
            </Typography>
          </Box>
        </Box>
        
        {/* Content Sections */}
        <Box sx={{ p: 3 }}>
          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid item xs={12} md={6}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 2,
                  height: '100%',
                  border: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <CardContent>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      pb: 1,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      mb: 2
                    }}
                  >
                    <PersonIcon color="primary" />
                    المعلومات الأساسية
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        اسم المستخدم
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        {user.username}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        الاسم الكامل
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        {user.fullName}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                      >
                        <EmailIcon fontSize="small" color="action" />
                        البريد الإلكتروني
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 2, color: 'primary.main' }}>
                        {user.email}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                      >
                        <PhoneIcon fontSize="small" color="action" />
                        رقم الهاتف
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        {user.phone || '-'}
                      </Typography>
                    </Grid>
                    
                    
                    <Grid item xs={12} sm={6}>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                      >
                        <AccessTimeIcon fontSize="small" color="action" />
                        آخر تسجيل دخول
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        {user.lastLogin ? formatDate(user.lastLogin) : 'لم يسجل الدخول بعد'}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                      >
                        <CalendarTodayIcon fontSize="small" color="action" />
                        تاريخ الإنشاء
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        {formatDate(user.createdAt)}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                      >
                        <CalendarTodayIcon fontSize="small" color="action" />
                        آخر تحديث
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        {formatDate(user.updatedAt)}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
            
          </Grid>
        </Box>
      </Paper>
      
      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          تأكيد حذف المستخدم
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            هل أنت متأكد من رغبتك في حذف هذا المستخدم؟ لا يمكن التراجع عن هذه العملية.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
            إلغاء
          </Button>
          <Button onClick={handleDelete} color="error" autoFocus>
            تأكيد الحذف
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserDetails;